<template>
  <div v-if="projection" class="space-y-4">
    <!-- Headline result -->
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          PY{{ projection.roster?.target_py || projection.roster?.year }} Projection
        </span>
      </div>
      <div class="p-4">
        <div class="flex items-center gap-6 mb-4">
          <div class="text-center">
            <div class="text-3xl font-bold" :class="p.savings_pct >= 0 ? 'text-emerald-600' : 'text-red-500'">
              {{ (p.savings_pct * 100).toFixed(1) }}%
            </div>
            <div class="text-xs text-gray-500 mt-1">Savings Rate</div>
          </div>
          <div class="text-center">
            <div class="text-3xl font-bold text-gray-900">{{ fmtNum(projection.roster?.total_benes) }}</div>
            <div class="text-xs text-gray-500 mt-1">Beneficiaries</div>
          </div>
          <div class="text-center">
            <div class="text-3xl font-bold text-gray-900">${{ fmtDollar(projection.benchmark?.risk_adjusted_benchmark_pmpy) }}</div>
            <div class="text-xs text-gray-500 mt-1">Benchmark PMPY</div>
          </div>
          <div class="text-center">
            <div class="text-3xl font-bold text-gray-900">${{ fmtDollar(projection.performance?.weighted_pmpy) }}</div>
            <div class="text-xs text-gray-500 mt-1">Actual PMPY</div>
          </div>
        </div>

        <!-- MSR status -->
        <div class="flex items-center gap-3 p-3 rounded-lg" :class="p.exceeds_msr ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'">
          <span class="text-lg font-bold" :class="p.exceeds_msr ? 'text-emerald-600' : 'text-red-500'">{{ p.exceeds_msr ? '✓' : '✗' }}</span>
          <div>
            <div class="text-sm font-medium" :class="p.exceeds_msr ? 'text-emerald-800' : 'text-red-800'">
              {{ p.exceeds_msr ? 'Exceeds MSR' : 'Below MSR' }}
            </div>
            <div class="text-xs" :class="p.exceeds_msr ? 'text-emerald-600' : 'text-red-600'">
              {{ (p.savings_pct * 100).toFixed(1) }}% savings vs {{ (p.msr * 100).toFixed(1) }}% threshold
              <span v-if="!p.exceeds_msr"> · Need to reduce PMPY by ${{ fmtDollar(Math.abs(p.pmpy_vs_breakeven || 0)) }}</span>
            </div>
          </div>
          <div class="flex-1"></div>
          <div v-if="p.loss_exposure > 0" class="text-right">
            <div class="text-sm font-semibold text-red-700">Loss exposure: ${{ fmtDollar(p.loss_exposure) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Expenditure breakdown -->
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Expenditure</span>
      </div>
      <div class="p-4">
        <div class="flex items-baseline gap-2 mb-3">
          <span class="text-2xl font-bold text-gray-900">${{ fmtDollar(projection.performance?.weighted_pmpy) }}</span>
          <span class="text-sm text-gray-500">PMPY</span>
        </div>
        <div class="space-y-1.5">
          <div v-for="cat in expenditureCategories" :key="cat.label" class="flex items-center gap-2">
            <span class="text-xs text-gray-500 w-16 text-right">{{ cat.label }}</span>
            <div class="flex-1 bg-gray-50 rounded-full h-4 overflow-hidden">
              <div class="h-full rounded-full" :class="cat.color" :style="{ width: cat.pct + '%' }"></div>
            </div>
            <span class="text-xs font-medium text-gray-900 w-16">${{ fmtDollar(cat.value) }}</span>
            <span class="text-xs text-gray-500 w-8 text-right">{{ cat.pct }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Trend -->
    <div v-if="projection.trend && projection.trend.length > 1" class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Performance Trend</span>
      </div>
      <div class="p-4">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
              <th class="pb-2 pr-4">Year</th>
              <th class="pb-2 pr-4 text-right">Benes</th>
              <th class="pb-2 pr-4 text-right">PMPY</th>
              <th class="pb-2 pr-4 text-right">YoY</th>
              <th class="pb-2 text-right">HCC</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(yr, idx) in projection.trend" :key="yr.year" class="border-b border-gray-200 last:border-0">
              <td class="py-1.5 pr-4">{{ yr.year }}</td>
              <td class="py-1.5 pr-4 text-right">{{ yr.total_benes?.toLocaleString() }}</td>
              <td class="py-1.5 pr-4 text-right">${{ fmtDollar(yr.weighted_pmpy) }}</td>
              <td class="py-1.5 pr-4 text-right" :class="yoy(idx) > 0 ? 'text-red-500' : yoy(idx) < 0 ? 'text-emerald-600' : ''">
                {{ idx > 0 ? (yoy(idx) > 0 ? '+' : '') + yoy(idx).toFixed(1) + '%' : '—' }}
              </td>
              <td class="py-1.5 text-right">{{ yr.weighted_hcc }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Cross-track comparison -->
    <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Track Comparison</span>
      </div>
      <div class="p-4">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
              <th class="pb-2 pr-4">Track</th>
              <th class="pb-2 pr-4 text-right">Sharing</th>
              <th class="pb-2 pr-4 text-right">Shared Savings</th>
              <th class="pb-2 pr-4 text-right">Loss Exposure</th>
              <th class="pb-2 text-right">Max Downside</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in trackComparison" :key="t.track" :class="[
              'border-b border-gray-200 last:border-0',
              t.track === projection.roster?.track ? 'bg-primary-600/5' : ''
            ]">
              <td class="py-2 pr-4 font-medium text-gray-900">
                {{ t.label }}
                <span v-if="t.track === projection.roster?.track" class="text-xs text-primary-600 ml-1">selected</span>
              </td>
              <td class="py-2 pr-4 text-right">{{ (t.sharing_rate * 100) }}%</td>
              <td class="py-2 pr-4 text-right font-medium" :class="t.shared_savings > 0 ? 'text-emerald-600' : 'text-gray-500'">
                {{ t.shared_savings > 0 ? '$' + fmtDollar(t.shared_savings) : '—' }}
              </td>
              <td class="py-2 pr-4 text-right" :class="t.loss_exposure > 0 ? 'text-red-500' : 'text-gray-500'">
                {{ t.loss_exposure > 0 ? '$' + fmtDollar(t.loss_exposure) : 'None' }}
              </td>
              <td class="py-2 text-right" :class="t.max_downside > 0 ? 'text-red-500' : 'text-gray-500'">
                {{ t.max_downside > 0 ? '$' + fmtDollar(t.max_downside) : 'None' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Data source note -->
    <div v-if="projection.roster?.data_source_note" class="text-xs text-gray-500 text-center">
      {{ projection.roster.data_source_note }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'AcoProjectionSummary',
  props: {
    projection: { type: Object, required: true },
  },
  computed: {
    p() { return this.projection?.projection || {} },
    expenditureCategories() {
      const perf = this.projection?.performance
      if (!perf) return []
      const total = perf.weighted_pmpy || 1
      const cats = []
      const add = (label, key, color) => {
        const value = perf[key]
        if (value != null) cats.push({ label, value, pct: Math.round((value / total) * 100), color })
      }
      add('IP', 'weighted_ip_pmpy', 'bg-red-400')
      add('SNF', 'weighted_snf_pmpy', 'bg-amber-400')
      add('OP', 'weighted_op_pmpy', 'bg-blue-400')
      add('PB', 'weighted_pb_pmpy', 'bg-emerald-400')
      add('HHA', 'weighted_hha_pmpy', 'bg-purple-400')
      add('HS', 'weighted_hs_pmpy', 'bg-pink-400')
      add('DME', 'weighted_dme_pmpy', 'bg-gray-400')
      return cats
    },
    trackComparison() {
      const bm = this.projection?.benchmark?.total_benchmark || 0
      const savPct = this.p.savings_pct || 0
      const msr = this.p.msr || 0
      const tracks = [
        { track: 'BASIC_A', label: 'BASIC A', sharing_rate: 0.40, savings_cap: 0.10, loss_rate: 0, loss_cap: 0 },
        { track: 'BASIC_B', label: 'BASIC B', sharing_rate: 0.40, savings_cap: 0.10, loss_rate: 0, loss_cap: 0 },
        { track: 'BASIC_C', label: 'BASIC C', sharing_rate: 0.50, savings_cap: 0.10, loss_rate: 0.30, loss_cap: 0.01 },
        { track: 'BASIC_D', label: 'BASIC D', sharing_rate: 0.50, savings_cap: 0.10, loss_rate: 0.30, loss_cap: 0.02 },
        { track: 'BASIC_E', label: 'BASIC E', sharing_rate: 0.50, savings_cap: 0.10, loss_rate: 0.30, loss_cap: 0.04 },
        { track: 'ENHANCED', label: 'ENHANCED', sharing_rate: 0.75, savings_cap: 0.20, loss_rate: 0.40, loss_cap: 0.15 },
      ]
      return tracks.map(t => {
        const gross = bm * savPct
        let shared = 0
        if (savPct >= msr) shared = Math.min(gross * t.sharing_rate, bm * t.savings_cap) * 0.98
        let loss = 0
        if (savPct < 0 && t.loss_rate > 0) loss = Math.min(Math.abs(gross) * t.loss_rate, bm * t.loss_cap)
        return { ...t, shared_savings: shared, loss_exposure: loss, max_downside: bm * t.loss_cap }
      })
    },
  },
  methods: {
    fmtNum(val) {
      if (val == null) return '—'
      if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M'
      if (val >= 1000) return (val / 1000).toFixed(1) + 'K'
      return val.toLocaleString()
    },
    fmtDollar(val) {
      if (val == null) return '—'
      return Math.round(val).toLocaleString()
    },
    yoy(idx) {
      const t = this.projection?.trend
      if (!t || idx === 0) return 0
      const prev = t[idx - 1]?.weighted_pmpy
      const curr = t[idx]?.weighted_pmpy
      return prev && curr ? ((curr - prev) / prev) * 100 : 0
    },
  },
}
</script>
